<template>
    <section class="list-prices">
        <div class="inner align-left">
            <h2 class="title h2">{{ $t('intro.title') }}</h2>
        </div>
        <div class="inner">
            <template v-for="({ list, history }, pKey) in actualData" :key="`group-${pKey}`">
                <p>{{ $t('intro.listPricesValidFrom', { date: parseDate(list.date) }) }}</p>
                <p>{{ $t('intro.pricePerLiterInclVat') }}</p>
                <table class="table">
                    <thead>
                        <tr>
                            <th><h4 class="h4">{{ $t(`product.${list.priceGroup}`) }}</h4></th>
                            <th><h4 class="h4">{{ parseDate(list.date) }}</h4></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="{ productCode, productName, unitPrice, currency } in list.products" :key="`row-${productCode}`">
                            <td>
                                {{ productName }}
                            </td>
                            <td>
                                {{ unitPrice ? `${unitPrice} ${currency}` : '' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <a v-if="history && history.countryCode !== 'NO'" class="download ds-btn ds-btn--primary" v-bind="historyLink(history)">{{ $t(`download.${history.priceGroup}`) }}</a>
            </template>
        </div>
    </section>
</template>
<script setup>
import { useNuxtApp, computed, useI18n } from '#imports';
import { DateTime } from 'luxon';
const { $i18n } = useNuxtApp();

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: '',
    },
    contentType: {
        type: String,
        required: false,
        default: '',
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    internalTitle: {
        type: String,
        required: false,
        default: '',
    },
    externalData: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    data: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const i18n = useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            intro: {
                title: 'St1 Stations',
                listPricesValidFrom: 'List prices valid from {date}',
                pricePerLiterInclVat: 'Price per liter incl. VAT',
            },
            table: {
                product: 'Product',
            },
            download: {
                fleet: 'Download price history for St1-Business (Excel)',
                CRT: 'Download price history for Truckdiesel (Excel)',
            },
        },
        'fi-FI': {
            intro: {
                title: 'St1 Asemat',
                listPricesValidFrom: 'Listahinnat haettu viimeksi {date}',
                pricePerLiterInclVat: 'Hinta per litra sis. ALV',
            },
            table: {
                product: 'Tuote',
            },
            download: {
                fleet: 'Lataa hintahistoria St1-Business (Excel)',
                CRT: 'Lataa hintahistoria Truckdiesel (Excel)',
            },
        },
        'sv-SE': {
            product: {
                CRT: 'Produkt',
                fleet: 'Produkt',
            },
            intro: {
                title: 'St1- och Shellstationer',
                listPricesValidFrom: 'Listpriser gällande från {date}',
                pricePerLiterInclVat: 'Pris per liter inkl. moms',
            },
            table: {
                product: 'Product',
            },
            download: {
                fleet: 'Ladda ner prishistorik för St1-Business (Excel)',
                CRT: 'Ladda ner prishistorik för Truckdiesel (Excel)',
                filename: 'Prishistorik',
            },
        },
        'no-NO': {
            product: {
                CRT: 'Produkt',
                fleet: 'Produkt',
            },
            intro: {
                title: 'St1 og Shell stasjoner',
                listPricesValidFrom: 'Listepriser oppdatert {date}',
                pricePerLiterInclVat: 'Pris per liter eks. mva',
            },
            table: {
                product: 'Product',
            },
            download: {
                fleet: 'Last ned prishistorikk for St1-Business (Excel)',
                CRT: 'Last ned prishistorikk for Truckdiesel (Excel)',
                filename: 'Prishistorikk',
            },
        },
    },
});

const actualData = computed(() => props.data?.current?.map((item, key) => ({ list: item, history: props.data?.history?.[key] })) || []);
const historyLink = history => ({
    download: `${i18n.t('download.filename')} ${parseDate(history.date)}.${history.fileExtension}`,
    href: `data:${history.mimeType};base64,${history.content}`,
});
const parseDate = date => DateTime.fromISO(date).setLocale($i18n.locale.value.split('-')[0]).toFormat('d LLL yyyy');
</script>
<style scoped>
.list-prices {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.list-prices .inner {
  justify-self: start;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.list-prices {
  padding: 4rem 0 3rem;
}

.download {
  margin-top: 1.5rem;
}

.h4 {
  text-transform: capitalize;
}

.title {
  margin-bottom: 2rem;
}

.table {
  width: 100%;
  max-width: calc(var(--desktop-max-width) / 2);
  margin-top: 2.5rem;
  border-collapse: collapse;
}

tr:not(:last-child) {
  border-bottom: 1px solid var(--color-base-grey-300);
}

th:first-child, td:first-child {
  width: 40%;
}

td, th {
  padding: 2rem 1.5rem 2rem 0;
}
@media (max-width: 991px) {
  td, th {
    padding: 1.25rem 1rem 1.25rem 0;
  }
}

@media (min-width: 992px) {
  .table {
    margin-top: 4rem;
  }
}
</style>
